import { useTheme } from "@repo/tokens";
import type {
  ButtonVariant,
  ButtonSize,
  ButtonStyles,
  ButtonShape,
} from "./types";
import type { CSSProperties } from "react";

export const useButtonStyles = (
  variant: ButtonVariant,
  size: ButtonSize,
  shape: ButtonShape,
  isDisabled: boolean,
  backgroundColor?: string,
  textColor?: string,
  height?: number | string,
): ButtonStyles => {
  const { theme } = useTheme();

  const heightMap: Record<ButtonSize, number> = {
    xs: 28,
    sm: theme.control.height.sm,
    md: theme.control.height.md,
    lg: theme.control.height.lg,
    xl: 56,
  };

  const borderRadiusMap: Record<ButtonShape, string | number> = {
    square: 0,
    rounded: theme.control.radius.md,
    circle: 9999,
  };

  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: {
      backgroundColor: theme.action.primary.default,
      color: theme.action.primary.label,
      border: "none",
    },
    secondary: {
      backgroundColor: theme.action.secondary.default,
      color: theme.action.secondary.label,
      border: "none",
    },
    outline: {
      backgroundColor: "transparent",
      color: theme.action.primary.default,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: theme.action.primary.default,
    },
    ghost: {
      backgroundColor: "transparent",
      color: theme.text.primary,
      border: "none",
    },
    navigation: {
      backgroundColor: "transparent",
      color: theme.action.primary.default,
      border: "none",
    },
    dashed: {
      backgroundColor: "transparent",
      color: theme.text.primary,
      borderStyle: "dashed",
      border: `1px dashed ${theme.action.primary.default}`,
    },
  };

  return {
    container: {
      height: height || heightMap[size],
      borderRadius: borderRadiusMap[shape],
      paddingInline: theme.control.padding.x.md,
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.6 : 1,

      ...variants[variant],
      ...(isDisabled && (variant === "outline" || variant === "dashed")
        ? { borderColor: theme.border.default }
        : {}),
      ...(backgroundColor ? { backgroundColor } : {}),
      ...(textColor ? { color: textColor } : {}),
    },

    spinnerColor:
      textColor ||
      (variant === "primary"
        ? theme.action.primary.label
        : theme.action.primary.default),
  };
};
